import { Big } from 'big.js';
import { TenantOrderLine } from '~/lib/model';
import BreMatterportConfig, { Outdoor } from '~/tenants/bre/performable/matterport/BreMatterportConfig';
import BreJob from '~/tenants/bre/model/BreJob';
import { BreOrderType } from '~/tenants/bre/model/enums';
import { FIRST_VERSION_TIMESTAMP, ZodVersionedMetadata } from '~/lib/zod';
import { BreMatterportPay, BreMatterportRate } from '~/tenants/bre/performable/matterport/BreMatterportData';
import BreFloorplanJob from '~/tenants/bre/performable/floorplan/BreFloorplanJob';
import { match } from 'ts-pattern';
import { DateTime } from 'luxon';
import { bre_version, BreVersion } from '~/tenants/bre/data';
import { bre_round } from '~/tenants/bre/performable/common';

export default class BreMatterportJob extends BreJob<typeof BreMatterportConfig> {
  get performable(): typeof BreMatterportConfig {
    return BreMatterportConfig;
  }

  get configurable(): boolean {
    return true;
  }

  get eligible(): boolean {
    return this.isOneOf(BreOrderType.RESIDENTIAL, BreOrderType.COMMERCIAL);
  }

  get sqft(): number {
    return this.metadata.sqft ?? super.sqft;
  }

  static rate(sqft: number, created: TDateISO | DateTime = DateTime.now()): Big {
    let amount = new Big('0.10').times(sqft - 1500).plus(300);

    if (sqft >= 4000) {
      amount = new Big('0.05').times(sqft - 4000).plus(500);
    }

    return bre_version(created, BreVersion.V1) ? bre_round(amount.times('1.05')) : amount.times('1.05');
  }

  static outdoorName(outdoor: Outdoor): string {
    return match(outdoor)
      .with('back', () => 'Back Yard')
      .with('front', () => 'Front Yard')
      .with('both', () => 'Front & Back Yard')
      .exhaustive();
  }

  defaultValue(): ZodVersionedMetadata<(typeof BreMatterportConfig)['schema']> {
    return {
      version: FIRST_VERSION_TIMESTAMP,
    };
  }

  onsite(): number {
    const { outdoor, outdoor_commercial } = this.metadata;
    const sqftTime = match(this.sqft)
      .when(
        (sqft) => sqft <= 0,
        () => 0,
      )
      .when(
        (sqft) => sqft <= 1500,
        () => 45,
      )
      .when(
        (sqft) => sqft <= 2500,
        () => 60,
      )
      .when(
        (sqft) => sqft <= 3500,
        () => 75,
      )
      .otherwise(() => 90);
    const outdoorTime = match(outdoor)
      .with('back', () => 20)
      .with('front', () => 20)
      .with('both', () => 40)
      .otherwise(() => 0);

    return sqftTime + outdoorTime + (outdoor_commercial ? 30 : outdoorTime);
  }

  revenueLines(): TenantOrderLine[] {
    const { type } = this.order.metadata;
    const { outdoor, outdoor_commercial, site_plan, plan, floorplan } = this.metadata;
    const { OUTDOOR_BACK, OUTDOOR_FRONT, OUTDOOR_BOTH, OUTDOOR_BOTH_XL, SITE_PLAN } = BreMatterportRate[type];
    const lines: TenantOrderLine[] = [];

    if (type !== BreOrderType.COMMERCIAL) {
      if (this.sqft > 0) {
        lines.push({
          id: '3d_matterport',
          description: '3D Scan',
          amount: bre_round(BreMatterportJob.rate(this.sqft, this.orderCreated)),
        });
      }

      if (type === BreOrderType.RESIDENTIAL && outdoor) {
        const { lotSqft } = this.order.metadata;

        if (lotSqft) {
          const rate = match(outdoor)
            .with('back', () => OUTDOOR_BACK)
            .with('front', () => OUTDOOR_FRONT)
            .with('both', () => (lotSqft <= 8000 ? OUTDOOR_BOTH : OUTDOOR_BOTH_XL))
            .exhaustive();

          lines.push({
            id: 'outdoor',
            description: `${BreMatterportJob.outdoorName(outdoor)} Scan`,
            amount: new Big(rate),
          });
        }
      }

      if (outdoor === 'both' && site_plan) {
        lines.push({
          id: 'site_plan',
          description: 'Site Plan',
          amount: new Big(SITE_PLAN),
        });
      }
    }

    if (type === BreOrderType.COMMERCIAL && plan) {
      if (this.sqft > 0) {
        lines.push({
          id: '3d_matterport',
          description: `3D scan (${plan})`,
          amount: bre_round(new Big(plan === 'basic' ? '0.07' : '0.09').times(this.sqft)),
        });
      }

      if (outdoor_commercial) {
        const rate = match(plan)
          .with('basic', () => OUTDOOR_BOTH)
          .with('advanced', () => OUTDOOR_BOTH_XL)
          .exhaustive();

        lines.push({
          id: 'outdoor_commercial',
          description: `Outdoor Matterport (${plan})`,
          amount: new Big(rate),
        });
      }
    }

    if (floorplan) {
      lines.push({
        id: '2d_floorplan',
        description: '2D Floorplan',
        amount: BreFloorplanJob.rate(this.sqft, type),
      });
    }

    return lines;
  }

  expenseLines(): TenantOrderLine[] {
    const { type } = this.order.metadata;
    const { outdoor, outdoor_commercial, site_plan, plan, floorplan } = this.metadata;
    const { OUTDOOR_BACK, OUTDOOR_FRONT, OUTDOOR_BOTH, OUTDOOR_BOTH_XL, SITE_PLAN } = BreMatterportPay[type];
    const lines: TenantOrderLine[] = [];

    if (type !== BreOrderType.COMMERCIAL) {
      if (this.sqft > 0) {
        lines.push({
          id: '3d_matterport',
          description: '3D Scan',
          amount: bre_round(new Big('0.04').times(this.sqft).plus('135')),
        });
      }

      if (type === BreOrderType.RESIDENTIAL && outdoor) {
        const { lotSqft } = this.order.metadata;

        if (lotSqft) {
          const rate = match(outdoor)
            .with('back', () => OUTDOOR_BACK)
            .with('front', () => OUTDOOR_FRONT)
            .with('both', () => (lotSqft <= 8000 ? OUTDOOR_BOTH : OUTDOOR_BOTH_XL))
            .exhaustive();

          lines.push({
            id: 'outdoor',
            description: `${BreMatterportJob.outdoorName(outdoor)} Scan`,
            amount: new Big(rate),
          });
        }
      }

      if (outdoor === 'both' && site_plan) {
        lines.push({
          id: 'site_plan',
          description: 'Site Plan',
          amount: new Big(SITE_PLAN),
        });
      }
    }

    if (type === BreOrderType.COMMERCIAL) {
      if (this.sqft > 0) {
        lines.push({
          id: '3d_matterport',
          description: `3D Scan (${plan})`,
          amount: new Big(plan === 'basic' ? '0.05' : '0.07').times(this.sqft),
        });

        if (outdoor_commercial) {
          lines.push({
            id: 'outdoor_commercial',
            description: `Outdoors Scan (${plan})`,
            amount: new Big(plan === 'basic' ? '600' : '1000'),
          });
        }
      }
    }

    if (floorplan) {
      lines.push({
        id: '2d_floorplan',
        description: '2D Floorplan',
        amount: BreFloorplanJob.pay(this.sqft, type),
      });
    }

    return lines;
  }
}
